/**
 * Rejection reason config — module-level fetch-once cache.
 *
 * Backend contract (onboarding-service):
 *   GET  <VITE_ONBOARDING_SERVICE>rejection_reasons_config
 *   →    { data: { items: [{ code, label, description }] } }
 *
 * The config is small (a handful of codes), rarely changes, and is looked
 * up from many places (order-history rejection banners, wallet strip
 * tooltips, dispatcher screens). Caching at module scope means every
 * consumer shares a single in-flight promise the first time it loads and
 * gets the cached map on every call after that.
 *
 * The cache is intentionally per-page-load — the config is small enough
 * that stale-during-a-session is fine, and we don't want to reason about
 * cache invalidation on a form field that ships with 5-10 static rows.
 */

import axios from 'axios'

export interface RejectionReason {
  code: string
  label: string
  description?: string
}

// The in-flight/cached promise. Populated on first call to
// loadRejectionReasons(); reused for every subsequent call.
let cache: Promise<Record<string, RejectionReason>> | null = null

/**
 * Fetch (or return from cache) the rejection-reason map keyed by code.
 * Never rejects — a failed fetch resolves to {} so callers can render a
 * fallback ("Reason unavailable") without try/catch scattered everywhere.
 */
export function loadRejectionReasons(): Promise<Record<string, RejectionReason>> {
  if (cache) return cache
  cache = (async () => {
    try {
      const url = `${import.meta.env.VITE_ONBOARDING_SERVICE}rejection_reasons_config`
      const res = await axios.get(url)
      const items: RejectionReason[] = res?.data?.data?.items ?? []
      const map: Record<string, RejectionReason> = {}
      for (const it of items) {
        if (it && typeof it.code === 'string') map[it.code] = it
      }
      return map
    } catch {
      // Swallow — the UI already degrades gracefully to the raw code /
      // note when the label lookup misses.
      return {}
    }
  })()
  return cache
}

/**
 * Synchronous lookup helper for a code once the config has been loaded.
 * Pass the map returned by loadRejectionReasons(); returns the pretty
 * label or the raw code as a last-resort fallback.
 */
export function labelForCode(
  map: Record<string, RejectionReason>,
  code: string | null | undefined,
): string {
  if (!code) return ''
  return map[code]?.label || code
}
